import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useReducedMotion } from 'framer-motion';
import { ModeSwitchTransition } from './ModeSwitchTransition';

/**
 * App-level owner of the Client <-> Helper mode-switch transition.
 *
 * Rendering the overlay here (once, above the routed pages) rather than inside
 * whichever control triggered the switch means it SURVIVES the navigation: the
 * intro plays, the route changes underneath it, then the outro fades it out to
 * reveal the new page. A control that rendered its own overlay would unmount on
 * navigate and kill the animation mid-way.
 *
 * Any switch control calls `playModeSwitch({ target, resolveDestination })`:
 *   - target: the mode being entered ('helper' | 'client') — drives the morph.
 *   - resolveDestination: async () => path. Runs while the intro plays, so any
 *     profile lookup needed to pick the destination is masked by the animation.
 * A busy ref makes concurrent triggers no-ops. Errors from resolveDestination
 * propagate to the caller (for its own toast) after the overlay is torn down.
 */

interface PlayArgs {
  target: 'helper' | 'client';
  resolveDestination: () => string | Promise<string>;
}

interface ModeSwitchContextValue {
  playModeSwitch: (args: PlayArgs) => Promise<void>;
  isSwitching: boolean;
}

const ModeSwitchContext = createContext<ModeSwitchContextValue | null>(null);

export function ModeSwitchProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<'helper' | 'client' | null>(null);
  const busyRef = useRef(false);
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const playModeSwitch = useCallback(
    async ({ target, resolveDestination }: PlayArgs) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setTarget(target); // overlay up immediately, masking the lookup below

      const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
      // Total on-screen time ~2.1s (intro + outro) so the switch reads as a
      // deliberate moment; trimmed to near-instant under reduced motion.
      const introMs = reduce ? 150 : 1250;
      const outroMs = reduce ? 100 : 900;

      try {
        // Resolve the destination while the intro animation plays; hold for at
        // least introMs so the morph is never cut short by a fast lookup.
        const [destination] = await Promise.all([
          Promise.resolve().then(resolveDestination),
          sleep(introMs),
        ]);
        navigate(destination);
        await sleep(outroMs);
      } finally {
        setTarget(null); // fades the overlay out, revealing the new page
        busyRef.current = false;
      }
    },
    [navigate, reduce],
  );

  return (
    <ModeSwitchContext.Provider value={{ playModeSwitch, isSwitching: target !== null }}>
      {children}
      <ModeSwitchTransition target={target} />
    </ModeSwitchContext.Provider>
  );
}

export function useModeSwitch(): ModeSwitchContextValue {
  const ctx = useContext(ModeSwitchContext);
  if (!ctx) {
    // Fail soft: outside the provider, switch controls just navigate normally.
    return { playModeSwitch: async () => {}, isSwitching: false };
  }
  return ctx;
}
